import type { ParsedCsv } from '../../AppAndSiteMarketplace.types';

const BOM = '﻿';

const detectDelimiter = (text: string): ',' | ';' => {
  const firstLine = text.split(/\r\n|\n|\r/, 1)[0] ?? '';
  return !firstLine.includes(',') && firstLine.includes(';') ? ';' : ',';
};

/** Splits CSV text into rows of cells. Supports quoted fields with `""` escapes, embedded delimiters and newlines. */
const parseRows = (text: string, delimiter: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char !== '"') {
        cell += char;
      } else if (text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = false;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      endCell();
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') {
        i += 1;
      }
      endRow();
    } else {
      cell += char;
    }
  }
  if (cell !== '' || row.length) {
    endRow();
  }

  return rows;
};

const isEmptyRow = (row: string[]) => row.every(cell => cell.trim() === '');

/**
 * Parses CSV text. Delimiter is a comma (a semicolon is accepted when the header has no commas); BOM, CRLF/LF and
 * empty lines are tolerated. The first non-empty row is the header. Returns `undefined` when there is no header.
 */
export const parseCsv = (rawText: string): ParsedCsv | undefined => {
  const text = rawText.startsWith(BOM) ? rawText.slice(BOM.length) : rawText;
  const [header, ...rows] = parseRows(text, detectDelimiter(text)).filter(row => !isEmptyRow(row));

  return header ? { header: header.map(cell => cell.trim()), rows } : undefined;
};
